import { Link } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useCRMEventsContext } from '@fhs/auth/src/hooks';
import { IconErrorFill, Text, XStack, tokens } from '@fhs/ui';
import { MaxCartTotalAlert } from '@fhs-legacy/frontend/src/components/max-cart-total-alert';
import OrderUnavailableModal from '@fhs-legacy/frontend/src/components/modal-order-unavailable';
import { useCartLimit } from '@fhs-legacy/frontend/src/hooks/use-cart-limit';
import useIsSignUpAfterCart from '@fhs-legacy/frontend/src/hooks/use-is-sign-up-after-cart';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { isDelivery } from '@fhs-legacy/frontend/src/utils/service-mode';

import useCartErrorLogging from './use-cart-error-logging';

interface ICartErrorAlertsProps {
  cartButtonLoading: boolean;
  setIsDisabledForErrors: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CartErrorAlerts({
  cartButtonLoading,
  setIsDisabledForErrors,
}: ICartErrorAlertsProps) {
  /*
   * The following alerts are handled here
   * 1. isCartOverLimit - Uses OVERRIDE_CHECKOUT_LIMIT flag for limit
   * 2. hasPriceError - pricing or getOrder failure
   * 3. unavailableCartEntries (as modal)
   * 4. isOrderingEnabled - uses ENABLE_ORDERING flag for fires
   * 6. isCartSubTotalUndefined - not an alert but used for race conditions
   * 7. isStoreOpenAndAvailable - open and available or closed with future ordering enabled
   * 8. checkoutPriceMinimum - uses OVERRIDE_CHECKOUT_DELIVERY_MINIMUM flag for minimum delivery total;
   * 9. No Donation selected - makes the user explicitly choose a donation amount
   */
  const { formatMessage } = useIntl();
  const { logErrorDialogView } = useCRMEventsContext();

  const {
    cartEntries,
    serverOrder,
    tryPricing,
    unavailableCartEntries,
    priceMutationError,
    priceOrderFailure,
    calculatedCartTotal,
    shouldSkipPriceOrder,
    selectedDonationCartEntry,
    isOrderingEnabled,
  } = useCartContext();

  const { serviceMode } = useServiceModeContext();

  const isDeliveryOrder = isDelivery(serviceMode);
  const isSignUpAfterCart = useIsSignUpAfterCart();

  const { isStoreAvailable } = useStoreContext();
  const { isOverCartLimit } = useCartLimit(); // TODO: rewrite this one

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculatedCartTotal
      : serverOrder?.cart?.subTotalCents;
  const isCartSubTotalNaN = cartSubTotal === null || cartSubTotal === undefined;
  const hasPriceError = !!priceMutationError || priceOrderFailure;

  const checkoutDeliveryPriceMinimum: number = useFlag('override-checkout-delivery-minimum');
  const donationAmount = selectedDonationCartEntry?.price ?? 0;
  const cartPriceTooLow = (cartSubTotal ?? 0) - donationAmount <= checkoutDeliveryPriceMinimum;

  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  useEffect(() => {
    setShowUnavailableItemsModal(!!unavailableCartEntries.length);
  }, [unavailableCartEntries.length]);

  const hasError =
    isOverCartLimit ||
    hasPriceError ||
    !isOrderingEnabled ||
    !isStoreAvailable ||
    (isDeliveryOrder && !isCartSubTotalNaN && cartPriceTooLow);

  useCartErrorLogging({
    hasError,
    hasPriceError,
    priceMutationError,
    priceOrderFailure,
    unavailableCartEntries,
    calculatedCartTotal,
    shouldSkipPriceOrder,
    isStoreAvailable,
    serviceMode,
    cartEntries,
    isDeliveryOrder,
    cartSubTotal,
    isCartSubTotalNaN,
    cartPriceTooLow,
    checkoutDeliveryPriceMinimum,
    donationAmount,
    isOverCartLimit,
    isOrderingEnabled,
    logErrorDialogView,
  });

  useEffect(() => {
    // disables the continue button in the event of an error
    setIsDisabledForErrors(hasError);
  }, [hasError, setIsDisabledForErrors]);

  // if no error, only render the unavailable items modal when needed
  if (!hasError || cartButtonLoading) {
    return (
      <>
        {showUnavailableItemsModal && (
          <OrderUnavailableModal
            items={unavailableCartEntries}
            onDismiss={handleUnavailableModalDismiss}
          />
        )}
      </>
    );
  }

  const renderErrorText = () => {
    switch (true) {
      case isOverCartLimit:
        return <MaxCartTotalAlert isMaxCartTotalBold fontSize={{ base: 'xs', desktop: 'md' }} />;
      case hasPriceError:
        return (
          <Text style={styles.alertText}>
            There was an error processing your order. Please try again. Error code: (Price Mutation
            Error)
          </Text>
        );
      case !isOrderingEnabled:
        return (
          <Text style={styles.alertText}>
            Ordering is currently unavailable, please try again later.
          </Text>
        );

      case !isStoreAvailable:
        return (
          <Text style={styles.alertText}>
            The restaurant you are trying to order from is currently unavailable.{' '}
            <Link href={routes.storeLocator} style={styles.link}>
              Change store
            </Link>
          </Text>
        );
      case isDeliveryOrder && !isCartSubTotalNaN && cartPriceTooLow:
        return (
          <Text style={styles.alertText}>
            {formatMessage(
              {
                id: 'deliveryMinumumNotReached',
              },
              {
                min: (checkoutDeliveryPriceMinimum / 100).toFixed(2),
              }
            )}
          </Text>
        );

      default:
        return (
          <Text style={styles.alertText}>
            There was an error processing your order. Please try again.
          </Text>
        );
    }
  };

  return (
    <>
      {showUnavailableItemsModal && (
        <OrderUnavailableModal
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
        />
      )}
      <View
        style={styles.alertContainer}
        accessibilityRole="alert"
        accessibilityLiveRegion="assertive"
        accessibilityLabel="Error message"
      >
        <XStack style={styles.alertStack}>
          <IconErrorFill size={20} color={tokens.colors.$error} style={{ marginTop: 2 }} />
          <View style={{ flex: 1 }}>{renderErrorText()}</View>
          {/* unavailableCartEntries needs modal for removing the unavailable items  */}
        </XStack>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  alertContainer: {
    backgroundColor: tokens.colors.$errorBackground,
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  alertStack: { alignItems: 'center', gap: 8 },
  alertText: { fontSize: 14, color: tokens.colors.$houseDark, flexWrap: 'wrap' },
  link: { textDecorationLine: 'underline' },
});
